import React, { useCallback, useEffect, useState } from 'react';

import _ from 'lodash';

import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { SERVER_TYPE, TIME_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { validateAdminConsolePass } from '@silverfort-nodelibs/common/utils/common';
import { SECURITY_MAIN_FIELDS } from '../../common/fields';
import { CSR_TYPES } from '../../common/certTypes';
import * as actions from '../../containers/Security/securityActions';
import Account from '../Controls/Account/Account';
import Certificate from '../Controls/Certificate/Certificate';
import TextField from '../Controls/TextInput/TextInput';
import styles from './SecurityMain.module.css';
import TimeField from '../Controls/TimeField/TimeField';
import NotificationBanner from '../../../Banners/NotificationBanner';
import Label from '../Controls/Label/Label';
import Button from '../Controls/Button/Button';
import { Icon, Icons, Tooltip, Validation, ValidationStatus } from '@silverfort/sf-components';
import ADEntitiesSelector from '../../../Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import { SELECTOR_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import secureAxios from '../../../../core/api/client/secureAxios';
import TextInput from '../Controls/TextInput/TextInput';
import useFeature from '../../hooks/featureFlags/useFeature';

const SESSION_TIME_TYPES = _.pick(TIME_TYPES, ['HOURS', 'MINUTES']);
const MAX_DR_DURATION = 99999;
const URL_TEMPLATE = '*************************';

const SecurityMain = ({ fields, dispatch, isProxyDataUpdated }) => {
    const adminConsoleAccount = fields[SECURITY_MAIN_FIELDS.ADMIN_CONSOLE_ACCOUNT.ID];
    const sessionTimeout = fields[SECURITY_MAIN_FIELDS.SESSION_TIMEOUT.ID];
    const certificateRequest = fields[SECURITY_MAIN_FIELDS.CERTIFICATE_REQUEST.ID];
    const certificate = fields[SECURITY_MAIN_FIELDS.CERTIFICATE.ID];
    const httpProxy = fields[SECURITY_MAIN_FIELDS.HTTP_PROXY.ID];
    const httpsProxy = fields[SECURITY_MAIN_FIELDS.HTTPS_PROXY.ID];
    const proxyAccount = fields[SECURITY_MAIN_FIELDS.PROXY_ACCOUNT.ID];
    const drRecovery = fields[SECURITY_MAIN_FIELDS.DR_RECOVERY.ID];

    const [showIpSelector, setShowIpSelector] = useState();
    const [allowCopy, setAllowCopy] = useState();
    const [copied, setCopied] = useState();

    useEffect(() => {
        setCopied(false);
    }, [drRecovery?.isDirty]);

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.UPDATE_SECURITY_MAIN,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const calcUrl = () => {
        if (!drRecovery.value?.shutdownLink) {
            return '';
        }

        if (!allowCopy || !drRecovery.isDirty) {
            return (
                URL_TEMPLATE +
                drRecovery.value.shutdownLink.slice(
                    drRecovery.value?.shutdownLink.length - 4,
                    drRecovery.value?.shutdownLink.length
                )
            );
        }

        return URL_TEMPLATE;
    };

    const handleDurationChange = (e) => {
        const intOrShort = !e.target.value.split('.')[1] || e.target.value.split('.')[1].length < 2;

        if (
            !e.target.value ||
            e.target.value === '0' ||
            (intOrShort && parseFloat(e.target.value) <= MAX_DR_DURATION)
        ) {
            dispatchUpdate(SECURITY_MAIN_FIELDS.DR_RECOVERY.ID, {
                ...drRecovery.value,
                duration: e.target.value
            });
        }
    };

    const handleCopy = () => {
        if (copied || !allowCopy) {
            return;
        }
        navigator.clipboard.writeText(drRecovery.value.shutdownLink);
        setAllowCopy(false);
        setCopied(true);
    };

    const generateShutdownLink = async () => {
        try {
            const res = await secureAxios.get('/settings/generateShutdownLink');
            dispatchUpdate(SECURITY_MAIN_FIELDS.DR_RECOVERY.ID, {
                ...drRecovery.value,
                shutdownLink: res.data.shutdownLink,
                shutdownKey: res.data.shutdownKey
            });
            setAllowCopy(true);
            setCopied(false);
        } catch (e) {
            console.error('Failed to generate shutdown link', e);
        }
    };

    const onAdminConsoleUserSave = useCallback(
        (fieldId, value) => {
            function InvalidPasswordException(message) {
                this.data = message;
                this.name = 'InvalidPasswordException';
            }
            const validationResults = validateAdminConsolePass(value.oldPass, value.pass, 'New password');
            if (!validationResults.results) {
                throw new InvalidPasswordException(validationResults.error);
            }

            dispatch({
                type: actions.UPDATE_SECURITY_MAIN,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const proxyFieldExists = httpProxy || httpsProxy || proxyAccount;
    const showProxyDivider = (adminConsoleAccount || sessionTimeout) && proxyFieldExists;
    const showCertDivider =
        (adminConsoleAccount || sessionTimeout || proxyFieldExists) && (certificateRequest || certificate);

    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const notSaasOrSupport = systemMode !== SYSTEM_MODES.SAAS && systemMode !== SYSTEM_MODES.SAAS_SUPPORT;

    return (
        <>
            {adminConsoleAccount && (
                <Account
                    field={adminConsoleAccount}
                    changed={onAdminConsoleUserSave}
                    validateOldPassword
                    passwordConfirmation
                />
            )}
            {sessionTimeout && (
                <TimeField field={sessionTimeout} changed={dispatchUpdate} options={SESSION_TIME_TYPES} />
            )}
            {showProxyDivider && <div className={styles.horizontalDivider} />}
            {!isProxyDataUpdated && notSaasOrSupport && (
                <div className="w-fit my-[1.5rem]">
                    <NotificationBanner
                        theme="warn"
                        height="50px"
                        title={<div className="mr-[1rem]">The proxy data has not been updated.</div>}
                    />
                </div>
            )}

            {httpProxy && <TextField field={httpProxy} changed={dispatchUpdate} />}
            {httpsProxy && <TextField field={httpsProxy} changed={dispatchUpdate} />}
            {proxyAccount && <Account field={proxyAccount} changed={dispatchUpdate} />}
            {showCertDivider && <div className={styles.horizontalDivider} />}
            {certificateRequest && certificate && (
                <Certificate
                    header={certificate.name}
                    desc="Upload a certificate to create a trusted connection to the Admin Console"
                    certificate={certificate}
                    certificateRequest={certificateRequest}
                    certType={CSR_TYPES.TOWER}
                    serverType={SERVER_TYPE.TOWER}
                    changed={dispatchUpdate}
                />
            )}
            {drRecovery && notSaasOrSupport && (
                <div>
                    <div className={styles.horizontalDivider + ' my-8'} />
                    <div className="flex flex-col gap-4">
                        <Label value={drRecovery.name} />
                        <div className="flex flex-col gap-2">
                            <div className="w-[400px] text-14 text-sf-dark-500 flex justify-between">
                                <div className="flex gap-1">
                                    <span>Backup IPs</span>
                                    {drRecovery.value?.ipPool?.length ? `(${drRecovery.value.ipPool.length})` : ''}
                                </div>
                                {!!drRecovery.value?.ipPool?.length && (
                                    <div
                                        onClick={() => setShowIpSelector(true)}
                                        className="text-sf-primary-500 cursor-pointer">
                                        Update
                                    </div>
                                )}
                            </div>
                            {drRecovery.value?.ipPool?.length ? (
                                <div className="flex flex-wrap gap-2 w-[400px] max-h-[400px] overflow-y-auto bg-sf-white py-2 px-3 border border-sf-gray-100 rounded-lg ">
                                    {drRecovery.value.ipPool.map((ip, i) => (
                                        <div
                                            key={i}
                                            className="px-2 h-6 text-12 text-sf-gray-800 bg-sf-gray-50 rounded">
                                            {ip.address}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Button
                                    className={'w-20 flex gap-1 items-center px-2 justify-center'}
                                    onClick={() => setShowIpSelector(true)}>
                                    <Icon className="text-sf-primary-500" icon={Icons.Add} /> Add IP
                                </Button>
                            )}
                        </div>

                        <div className="flex gap-1 text-14 text-sf-dark-500 items-center">
                            <span>Duration of node availability while Admin Console is down:</span>
                            <Tooltip
                                className="w-fit"
                                title="Silverfort nodes provide high availability for your Silverfort setup. Choose the duration 
of the node availability in case of interruptions to the Admin Console.">
                                <Icon className="text-16 text-sf-gray-500 cursor-pointer" icon={Icons.CircleError} />
                            </Tooltip>
                        </div>

                        <div className="flex text-14 text-sf-dark-500 gap-2">
                            <input
                                onChange={handleDurationChange}
                                type="number"
                                placeholder={`0.5 - ${MAX_DR_DURATION}`}
                                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-0 w-18 bg-transparent border-b border-sf-light-500 text-sf-dark-500 "
                                value={drRecovery.value?.duration || ''}
                            />
                            Hours
                        </div>
                        {drRecovery.error && (
                            <div>
                                <Validation validationStatus={ValidationStatus.Error} text={drRecovery.error} />
                            </div>
                        )}
                    </div>
                    <div className="pt-8">
                        <Label value={'One-time link for emergency Silverfort shut down'} />

                        <div className="relative w-[440px]">
                            <TextInput
                                className="my-2"
                                type="text"
                                disabled
                                field={{
                                    value: calcUrl()
                                }}>
                                <span className="flex gap-1.5 ml-[-10px]">
                                    <Button onClick={generateShutdownLink}>
                                        <span className="flex gap-1 items-center">
                                            <Icon className="text-16 text-sf-primary-500" icon={Icons.Refresh} />
                                            {`Generate`}
                                        </span>
                                    </Button>
                                </span>
                            </TextInput>
                            <Icon
                                className={`${
                                    allowCopy && !copied && drRecovery.value.shutdownLink
                                        ? 'text-sf-dark-500 cursor-pointer'
                                        : 'text-sf-light-500 cursor-not-allowed'
                                } absolute right-[122px] top-[11px] z-100`}
                                onClick={handleCopy}
                                icon={Icons.Copy}
                            />
                        </div>
                        {drRecovery.isDirty && allowCopy && !copied && (
                            <p className="text-13 text-sf-dark-500">
                                Save the shut down link. You won’t be able to see it again
                            </p>
                        )}
                        {copied && <p className="text-13 text-sf-negative-500">Link copied</p>}
                    </div>
                    {showIpSelector && (
                        <ADEntitiesSelector
                            selectedObjectEntities={[]}
                            disableNetMasks={true}
                            selectedIpEntities={drRecovery.value?.ipPool || []}
                            selectorType={SELECTOR_TYPES.DEVICES}
                            showModal={showIpSelector}
                            handleCloseModal={() => {
                                setShowIpSelector(false);
                            }}
                            saveEntities={({ ipEntities }) => {
                                dispatchUpdate(SECURITY_MAIN_FIELDS.DR_RECOVERY.ID, {
                                    ...drRecovery.value,
                                    ipPool: ipEntities
                                });
                                setShowIpSelector(false);
                            }}
                            ipOnly={true}
                            allowIps={true}
                            disableAutoComplete={true}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default React.memo(SecurityMain);
